import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { MapDispatchToProps } from '~src/app/utils/ConnectUtils';
import { enterSpace } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { AllServerUsersSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { ConnectedQueryParamUpdateProvider } from '../../RouterHelpers/QueryParamUpdateProvider';
import { AllSiteUsersAndIdentitiesSpaceOwnProps } from './AllSiteUsersAndIdentitiesSpace';
import { ConnectedAllSiteUsersAndIdentitiesSpace } from './ConnectedAllSiteUsersAndIdentitiesSpace';

export interface AllSiteUsersAndIdentitiesSpaceProviderContainerOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly subspaceName: AllServerUsersSubspaceName;

  readonly siteName: string;

  readonly renderSpace: (props: AllSiteUsersAndIdentitiesSpaceOwnProps) => JSX.Element;
}

export interface AllSiteUsersAndIdentitiesSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
}

export type AllSiteUsersAndIdentitiesSpaceProviderContainerProps = AllSiteUsersAndIdentitiesSpaceProviderContainerOwnProps & AllSiteUsersAndIdentitiesSpaceProviderDispatchProps;

export class AllSiteUsersAndIdentitiesSpaceProviderContainer extends React.PureComponent<AllSiteUsersAndIdentitiesSpaceProviderContainerProps> {
  public render() {
    return this.props.renderSpace(this.props);
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: AllSiteUsersAndIdentitiesSpaceProviderContainerProps) {
    // This is a workaround because ConnectedSpaceInfoProvider does not have logic for enter space on component update.
    if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  private changeSubspace() {
    this.props.enterSpace(
      {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'siteusers',
        subspaceName: this.props.subspaceName,
        // Identities subspace does not support infinite scroll feature.
        isKeysetPaginationSpace: FeatureFlags.UserGridInfiniteScrollEnabled && this.props.subspaceName === 'list'
      }
    );

    this.props.requestSpaceContents();
  }
}

const mapDispatchToProps: MapDispatchToProps<AllSiteUsersAndIdentitiesSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
}, dispatch);

export const ConnectedAllSiteUsersAndIdentitiesSpaceProviderContainer: React.ComponentClass<AllSiteUsersAndIdentitiesSpaceProviderContainerOwnProps>
  = connect(undefined, mapDispatchToProps)(AllSiteUsersAndIdentitiesSpaceProviderContainer);

const renderSpace = (props: AllSiteUsersAndIdentitiesSpaceOwnProps): JSX.Element => <ConnectedAllSiteUsersAndIdentitiesSpace {...props} />;

export interface AllSiteUsersAndIdentitiesSpaceProviderOwnProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}
export type AllSiteUsersAndIdentitiesSpaceProviderProps = AllSiteUsersAndIdentitiesSpaceProviderOwnProps & Omit<AllSiteUsersAndIdentitiesSpaceProviderContainerOwnProps, 'renderSpace'>;

export const AllSiteUsersAndIdentitiesSpaceProvider: React.FunctionComponent<AllSiteUsersAndIdentitiesSpaceProviderProps> = (props) => {

  const spaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'siteusers',
    subspaceName: props.subspaceName,
    // Identities subspace does not support infinite scroll feature.
    isKeysetPaginationSpace: FeatureFlags.UserGridInfiniteScrollEnabled && props.subspaceName === 'list'
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={true}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedAllSiteUsersAndIdentitiesSpaceProviderContainer {...props} renderSpace={renderSpace}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};
